import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { portalLogger } from './utils/portalLogger';
import PageLogger from './components/PageLogger';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import CreatePassword from './pages/CreatePassword';
import UpdatePassword from './pages/UpdatePassword';
import MainLayout from './components/MainLayout';
import Dashboard from './pages/Dashboard';
import UploadPDFs from './pages/UploadPDFs';
import ExtractData from './pages/ExtractData';
import FieldMappings from './pages/FieldMappings';
import AuditLogs from './pages/AuditLogs';
import DatabaseManagement from './pages/DatabaseManagement';
import WorkOrdersList from './components/WorkOrdersList';
import CreateWorkOrder from './components/CreateWorkOrder';
import WorkOrderDetail from './components/WorkOrderDetail';
import ExtractionReview from './components/ExtractionReview';
import Analytics from './pages/Analytics';
import Users from './pages/Users';
import Sessions from './pages/Sessions';
import Settings from './pages/Settings';
import APILogs from './pages/APILogs';
import PortalLogs from './pages/PortalLogs';
import BulkCreate from './pages/BulkCreate';
import BulkExport from './pages/BulkExport';
import BatchExtract from './pages/BatchExtract';
import BatchDetail from './pages/BatchDetail';
import UsageReport from './pages/UsageReport';
import EmailWorkOrders from './pages/EmailWorkOrders';
import PurgeRecords from './pages/PurgeRecords';
import OriginalTemplates from './pages/OriginalTemplates';
import AMCManagement from './pages/AMCManagement';
import CancellationForm from './pages/CancellationForm';
// V1 Components (XOrbit/VJA Pipeline)
import V1Layout from './components/V1Layout';
import V1Dashboard from './components/V1Dashboard';
import V1WorkOrdersList from './components/V1WorkOrdersList';
import V1WorkOrderDetail from './components/V1WorkOrderDetail';
import V1Comparison from './components/V1Comparison';
import V1ComparisonDetail from './components/V1ComparisonDetail';
import V3WorkOrders from './pages/V3WorkOrders';
import V4WorkOrders from './pages/V4WorkOrders';
// COCB Components (Change of Distributor Pipeline)
import COCBLayout from './components/COCBLayout';
import COCBDashboard from './components/COCBDashboard';
import COCBWorkOrdersList from './components/COCBWorkOrdersList';
import COCBWorkOrderDetail from './components/COCBWorkOrderDetail';
// GFS Components (GFS TA Ops - Manulife OCR Pipeline)
import GFSLayout from './components/GFSLayout';
import GFSDashboard from './components/GFSDashboard';
import GFSWorkOrdersList from './components/GFSWorkOrdersList';
import GFSWorkOrderDetail from './components/GFSWorkOrderDetail';
import GFSUsage from './components/GFSUsage';
import GFSV1WorkOrders from './components/GFSV1WorkOrders';
import GFSV2WorkOrders from './components/GFSV2WorkOrders';
import GFSV1Docs from './components/GFSV1Docs';
import GFSV2Docs from './components/GFSV2Docs';
// SIP Components (SIP OCR Pipeline - SIP Form Processing)
import SIPLayout from './components/SIPLayout';
import SIPDashboard from './components/SIPDashboard';
import SIPWorkOrdersList from './components/SIPWorkOrdersList';
import SIPWorkOrderDetail from './components/SIPWorkOrderDetail';
// COB Components (Change of Bank Pipeline - Cheque Extraction)
import COBLayout from './components/COBLayout';
import COBDashboard from './components/COBDashboard';
import COBWorkOrderDetail from './components/COBWorkOrderDetail';
import COBExtractV1 from './components/COBExtractV1';
import COBExtractV2 from './components/COBExtractV2';
import COBWorkOrdersV1 from './components/COBWorkOrdersV1';
import COBWorkOrdersV2 from './components/COBWorkOrdersV2';
import COBWorkOrdersV3 from './components/COBWorkOrdersV3';
import COBProcessWorkOrdersV1 from './components/COBProcessWorkOrdersV1';
import COBProcessWorkOrdersV2 from './components/COBProcessWorkOrdersV2';
// Multi-AMC Components (Multi-AMC Extraction Pipeline)
import MultiAMCLayout from './components/MultiAMCLayout';
import MultiAMCDashboard from './components/MultiAMCDashboard';
import MultiAMCWorkOrders from './components/MultiAMCWorkOrders';
import MultiAMCWorkOrderDetail from './components/MultiAMCWorkOrderDetail';
// SIP/OTM Components (SIP & OTM Mandate Form Extraction Pipeline)
import SIPOTMLayout from './components/SIPOTMLayout';
import SIPOTMDashboard from './components/SIPOTMDashboard';
import SIPOTMWorkOrdersList from './components/SIPOTMWorkOrdersList';
import SIPOTMWorkOrderDetail from './components/SIPOTMWorkOrderDetail';
import './styles/GlobalStyles.css';
import './App.css';

// Helper function to get default dashboard based on user's pipeline access
const getDefaultDashboard = (user) => {
  if (!user) return '/login';
  const pipeline = user.pipeline || 'all';
  if (pipeline === 'v1') {
    return '/v1/dashboard';
  } else if (pipeline === 'cocb') {
    return '/v1/cocb/dashboard';
  } else if (pipeline === 'gfs') {
    return '/gfs/dashboard';
  } else if (pipeline === 'sip') {
    return '/sip/dashboard';
  } else if (pipeline === 'cob') {
    return '/cob/dashboard';
  } else if (pipeline === 'multi-amc') {
    return '/multi-amc/dashboard';
  } else if (pipeline === 'sip-otm') {
    return '/sip-otm/dashboard';
  }
  return '/dashboard';
};

// Helper function to check if user can access GLIB pipeline
const canAccessGlib = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'glib';
};

// Helper function to check if user can access V1 pipeline
const canAccessV1 = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'v1';
};

// Helper function to check if user can access COCB pipeline
const canAccessCOCB = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'cocb';
};

// Helper function to check if user can access GFS pipeline
const canAccessGFS = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'gfs';
};

// Helper function to check if user can access SIP pipeline
const canAccessSIP = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'sip';
};

// Helper function to check if user can access COB pipeline
const canAccessCOB = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'cob';
};

// Helper function to check if user can access Multi-AMC pipeline
const canAccessMultiAMC = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'multi-amc';
};

// Helper function to check if user can access SIP/OTM pipeline
const canAccessSIPOTM = (user) => {
  if (!user) return false;
  const pipeline = user.pipeline || 'all';
  return pipeline === 'all' || pipeline === 'sip-otm';
};

// Loading spinner component for protected routes
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '100vh',
    background: '#f3f4f6'
  }}>
    <div className="spinner"></div>
  </div>
);

// Protected route wrapper for GLIB pipeline (main dashboard)
const GlibProtectedRoute = ({ user, loading, children }) => {
  // Wait for session restoration before redirecting
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessGlib(user)) {
    // Redirect to appropriate dashboard based on pipeline
    if (canAccessV1(user)) return <Navigate to="/v1/dashboard" replace />;
    if (canAccessCOCB(user)) return <Navigate to="/v1/cocb/dashboard" replace />;
    if (canAccessGFS(user)) return <Navigate to="/gfs/dashboard" replace />;
    if (canAccessSIP(user)) return <Navigate to="/sip/dashboard" replace />;
    if (canAccessCOB(user)) return <Navigate to="/cob/dashboard" replace />;
    if (canAccessMultiAMC(user)) return <Navigate to="/multi-amc/dashboard" replace />;
    if (canAccessSIPOTM(user)) return <Navigate to="/sip-otm/dashboard" replace />;
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Protected route wrapper for V1 pipeline
const V1ProtectedRoute = ({ user, loading, children }) => {
  // Wait for session restoration before redirecting
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessV1(user)) {
    if (canAccessCOCB(user)) return <Navigate to="/v1/cocb/dashboard" replace />;
    if (canAccessGFS(user)) return <Navigate to="/gfs/dashboard" replace />;
    if (canAccessSIP(user)) return <Navigate to="/sip/dashboard" replace />;
    if (canAccessCOB(user)) return <Navigate to="/cob/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected route wrapper for COCB pipeline
const COCBProtectedRoute = ({ user, loading, children }) => {
  // Wait for session restoration before redirecting
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessCOCB(user)) {
    if (canAccessV1(user)) return <Navigate to="/v1/dashboard" replace />;
    if (canAccessGFS(user)) return <Navigate to="/gfs/dashboard" replace />;
    if (canAccessSIP(user)) return <Navigate to="/sip/dashboard" replace />;
    if (canAccessCOB(user)) return <Navigate to="/cob/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected route wrapper for GFS pipeline
const GFSProtectedRoute = ({ user, loading, children }) => {
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessGFS(user)) {
    if (canAccessV1(user)) return <Navigate to="/v1/dashboard" replace />;
    if (canAccessCOCB(user)) return <Navigate to="/v1/cocb/dashboard" replace />;
    if (canAccessSIP(user)) return <Navigate to="/sip/dashboard" replace />;
    if (canAccessCOB(user)) return <Navigate to="/cob/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected route wrapper for SIP pipeline
const SIPProtectedRoute = ({ user, loading, children }) => {
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessSIP(user)) {
    if (canAccessV1(user)) return <Navigate to="/v1/dashboard" replace />;
    if (canAccessCOCB(user)) return <Navigate to="/v1/cocb/dashboard" replace />;
    if (canAccessGFS(user)) return <Navigate to="/gfs/dashboard" replace />;
    if (canAccessCOB(user)) return <Navigate to="/cob/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected route wrapper for COB pipeline (Change of Bank - Cheque Extraction)
const COBProtectedRoute = ({ user, loading, children }) => {
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessCOB(user)) {
    if (canAccessV1(user)) return <Navigate to="/v1/dashboard" replace />;
    if (canAccessCOCB(user)) return <Navigate to="/v1/cocb/dashboard" replace />;
    if (canAccessGFS(user)) return <Navigate to="/gfs/dashboard" replace />;
    if (canAccessSIP(user)) return <Navigate to="/sip/dashboard" replace />;
    if (canAccessMultiAMC(user)) return <Navigate to="/multi-amc/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected route wrapper for Multi-AMC pipeline
const MultiAMCProtectedRoute = ({ user, loading, children }) => {
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessMultiAMC(user)) {
    if (canAccessV1(user)) return <Navigate to="/v1/dashboard" replace />;
    if (canAccessCOCB(user)) return <Navigate to="/v1/cocb/dashboard" replace />;
    if (canAccessGFS(user)) return <Navigate to="/gfs/dashboard" replace />;
    if (canAccessSIP(user)) return <Navigate to="/sip/dashboard" replace />;
    if (canAccessCOB(user)) return <Navigate to="/cob/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Protected route wrapper for SIP/OTM pipeline (SIP & OTM Mandate Form Extraction)
const SIPOTMProtectedRoute = ({ user, loading, children }) => {
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!canAccessSIPOTM(user)) {
    if (canAccessV1(user)) return <Navigate to="/v1/dashboard" replace />;
    if (canAccessCOCB(user)) return <Navigate to="/v1/cocb/dashboard" replace />;
    if (canAccessGFS(user)) return <Navigate to="/gfs/dashboard" replace />;
    if (canAccessSIP(user)) return <Navigate to="/sip/dashboard" replace />;
    if (canAccessCOB(user)) return <Navigate to="/cob/dashboard" replace />;
    if (canAccessMultiAMC(user)) return <Navigate to="/multi-amc/dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token') || localStorage.getItem('authToken');
    
    console.log('[App] Session check - storedUser:', !!storedUser, 'storedToken:', !!storedToken);
    
    // Must have BOTH user data AND token for valid session
    if (!storedUser || !storedToken) {
      console.log('[App] Missing user or token, clearing session and redirecting to login');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      const userData = JSON.parse(storedUser);
      // Ensure required fields exist - default pipeline to 'all' for existing users
      if (!userData.email) {
        console.log('[App] User data missing email, clearing stale session');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        setUser(null);
      } else {
        // Backfill pipeline for existing users who don't have it
        if (!userData.pipeline) {
          userData.pipeline = 'all';
          localStorage.setItem('user', JSON.stringify(userData));
        }
        console.log('[App] Valid session found for:', userData.email, 'pipeline:', userData.pipeline);
        setUser(userData);
        portalLogger.logAction('USER_SESSION_RESTORED', `User ${userData.email} session restored (pipeline: ${userData.pipeline})`);
      }
    } catch (err) {
      console.log('[App] Invalid user data in localStorage, clearing session');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      portalLogger.logError('SESSION_RESTORE_ERROR', err.message);
    }
    setLoading(false);

    // Set up global error handler
    const handleError = (event) => {
      portalLogger.logError('JAVASCRIPT_ERROR', event.message || 'Unknown error');
    };

    const handleUnhandledRejection = (event) => {
      portalLogger.logError('UNHANDLED_PROMISE_REJECTION', event.reason?.message || String(event.reason));
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    portalLogger.logAction('USER_LOGIN', `User ${userData.email} logged in`);
    // Note: Navigation is handled by Login.js component after setting state
  };

  const handleLogout = () => {
    const userEmail = user?.email || 'Unknown';
    setUser(null);
    // Clear all session data
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    portalLogger.logAction('USER_LOGOUT', `User ${userEmail} logged out`);
  };

  if (loading) {
    return (
      <div className="app loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <PageLogger />
      <Routes>
        <Route
          path="/login"
          element={
            user ? <Navigate to={getDefaultDashboard(user)} replace /> : <Login onLogin={handleLogin} />
          }
        />

        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/create-password" element={<CreatePassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        <Route
          path="/"
          element={
            user ? <Navigate to={getDefaultDashboard(user)} replace /> : <Navigate to="/login" replace />
          }
        />

        {/* GLIB Pipeline Routes - Protected for glib/all users only */}
        <Route
          element={
            <GlibProtectedRoute user={user} loading={loading}>
              <MainLayout user={user} onLogout={handleLogout} />
            </GlibProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/upload" element={<UploadPDFs />} />
          <Route path="/extract" element={<ExtractData />} />
          <Route path="/mappings" element={<FieldMappings />} />
          <Route path="/audit" element={<AuditLogs />} />
          <Route path="/database" element={<DatabaseManagement />} />
          {/* Work Order Routes */}
          <Route path="/work-orders" element={<WorkOrdersList />} />
          <Route path="/create-work-order" element={<CreateWorkOrder />} />
          <Route path="/work-order/:workOrderId" element={<WorkOrderDetail />} />
          <Route path="/work-order/:workOrderId/extraction/:extractionId" element={<ExtractionReview />} />

          {/* Main Operations */}
          <Route path="/bulk-create" element={<BulkCreate />} />
          <Route path="/bulk-export" element={<BulkExport />} />
          <Route path="/batch-extract" element={<BatchExtract />} />
          <Route path="/batch/:batchId" element={<BatchDetail />} />
          <Route path="/email-work-orders" element={<EmailWorkOrders />} />

          {/* Management Pages */}
          <Route path="/sessions" element={<Sessions />} />
          <Route path="/users" element={<Users />} />
          <Route path="/usage-report" element={<UsageReport />} />
          <Route path="/analytics" element={<Analytics />} />

          {/* System Pages */}
          <Route path="/original-templates" element={<OriginalTemplates />} />
          <Route path="/amc-management" element={<AMCManagement />} />
          <Route path="/cancellation-forms" element={<CancellationForm />} />
          <Route path="/purge-records" element={<PurgeRecords />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/api-logs" element={<APILogs />} />
          <Route path="/portal-logs" element={<PortalLogs />} />
        </Route>

        {/* V1 Pipeline - Completely Separate Layout - Protected for v1/all users only */}
        <Route
          element={
            <V1ProtectedRoute user={user} loading={loading}>
              <V1Layout user={user} onLogout={handleLogout} />
            </V1ProtectedRoute>
          }
        >
          <Route path="/v1" element={<V1Dashboard />} />
          <Route path="/v1/dashboard" element={<V1Dashboard />} />
          <Route path="/v1/xorbit" element={<V1WorkOrdersList modelType="xorbit" />} />
          <Route path="/v1/vja" element={<V1WorkOrdersList modelType="vja" />} />
          <Route path="/v1/v3-workorders" element={<V3WorkOrders />} />
          <Route path="/v1/v4-workorders" element={<V4WorkOrders />} />
          <Route path="/v1/v3/work-order/:workOrderId" element={<V1WorkOrderDetail />} />
          <Route path="/v1/comparison" element={<V1Comparison />} />
          <Route path="/v1/comparison/:comparisonId" element={<V1ComparisonDetail />} />
          <Route path="/v1/work-order/:workOrderId" element={<V1WorkOrderDetail />} />
          <Route path="/v1/stats" element={<V1Dashboard />} />
          <Route path="/v1/api-logs" element={<APILogs />} />
          <Route path="/v1/portal-logs" element={<PortalLogs />} />
        </Route>

        {/* COCB Pipeline - Separate Layout for Change of Distributor - Protected for cocb/all users only */}
        <Route
          element={
            <COCBProtectedRoute user={user} loading={loading}>
              <COCBLayout user={user} onLogout={handleLogout} />
            </COCBProtectedRoute>
          }
        >
          <Route path="/v1/cocb" element={<COCBDashboard />} />
          <Route path="/v1/cocb/dashboard" element={<COCBDashboard />} />
          <Route path="/v1/cocb/extract" element={<COCBDashboard />} />
          <Route path="/v1/cocb/history" element={<COCBWorkOrdersList />} />
          <Route path="/v1/cocb/work-order/:workOrderId" element={<COCBWorkOrderDetail />} />
          <Route path="/v1/cocb/api-logs" element={<APILogs />} />
        </Route>

        {/* GFS Pipeline - GFS TA Ops Manulife OCR - Protected for gfs/all users only */}
        <Route
          element={
            <GFSProtectedRoute user={user} loading={loading}>
              <GFSLayout user={user} onLogout={handleLogout} />
            </GFSProtectedRoute>
          }
        >
          <Route path="/gfs" element={<GFSDashboard />} />
          <Route path="/gfs/dashboard" element={<GFSDashboard />} />
          <Route path="/gfs/extract" element={<GFSDashboard />} />
          <Route path="/gfs/history" element={<GFSWorkOrdersList />} />
          <Route path="/gfs/usage" element={<GFSUsage />} />
          <Route path="/gfs/api-logs" element={<APILogs />} />
          <Route path="/gfs/work-order/:workOrderId" element={<GFSWorkOrderDetail />} />
          {/* GFS V1 Pipeline Routes */}
          <Route path="/gfs/v1/work-orders" element={<GFSV1WorkOrders />} />
          <Route path="/gfs/v1/work-order/:workOrderId" element={<GFSWorkOrderDetail />} />
          <Route path="/gfs/v1/docs" element={<GFSV1Docs />} />
          {/* GFS V2 Pipeline Routes */}
          <Route path="/gfs/v2/work-orders" element={<GFSV2WorkOrders />} />
          <Route path="/gfs/v2/work-order/:workOrderId" element={<GFSWorkOrderDetail />} />
          <Route path="/gfs/v2/docs" element={<GFSV2Docs />} />
        </Route>

        {/* SIP Pipeline - SIP OCR Form Processing - Protected for sip/all users only */}
        <Route
          element={
            <SIPProtectedRoute user={user} loading={loading}>
              <SIPLayout user={user} onLogout={handleLogout} />
            </SIPProtectedRoute>
          }
        >
          <Route path="/sip" element={<SIPDashboard />} />
          <Route path="/sip/dashboard" element={<SIPDashboard />} />
          <Route path="/sip/extract" element={<SIPDashboard />} />
          <Route path="/sip/history" element={<SIPWorkOrdersList />} />
          <Route path="/sip/work-order/:workOrderId" element={<SIPWorkOrderDetail />} />
          <Route path="/sip/api-logs" element={<APILogs />} />
        </Route>

        {/* COB Pipeline - Change of Bank Cheque Extraction - Protected for cob/all users only */}
        <Route
          element={
            <COBProtectedRoute user={user} loading={loading}>
              <COBLayout user={user} onLogout={handleLogout} />
            </COBProtectedRoute>
          }
        >
          <Route path="/cob" element={<COBDashboard />} />
          <Route path="/cob/dashboard" element={<COBDashboard />} />
          <Route path="/cob/extract" element={<COBDashboard />} />
          <Route path="/cob/extract/v1" element={<COBExtractV1 />} />
          <Route path="/cob/extract/v2" element={<COBExtractV2 />} />
          <Route path="/cob/v1-workorders" element={<COBWorkOrdersV1 />} />
          <Route path="/cob/v2-workorders" element={<COBWorkOrdersV2 />} />
          <Route path="/cob/v3-workorders" element={<COBWorkOrdersV3 />} />
          <Route path="/cob/v1-process-workorders" element={<COBProcessWorkOrdersV1 />} />
          <Route path="/cob/v2-process-workorders" element={<COBProcessWorkOrdersV2 />} />
          <Route path="/cob/process-work-order/:workOrderId" element={<COBWorkOrderDetail />} />
          <Route path="/cob/work-order/:workOrderId" element={<COBWorkOrderDetail />} />
          <Route path="/cob/api-logs" element={<APILogs />} />
        </Route>

        {/* Multi-AMC Pipeline - Multi-AMC Extraction - Protected for multi-amc/all users only */}
        <Route
          element={
            <MultiAMCProtectedRoute user={user} loading={loading}>
              <MultiAMCLayout user={user} onLogout={handleLogout} />
            </MultiAMCProtectedRoute>
          }
        >
          <Route path="/multi-amc" element={<MultiAMCDashboard />} />
          <Route path="/multi-amc/dashboard" element={<MultiAMCDashboard />} />
          <Route path="/multi-amc/extract" element={<MultiAMCDashboard />} />
          <Route path="/multi-amc/work-orders" element={<MultiAMCWorkOrders />} />
          <Route path="/multi-amc/work-orders/:workOrderId" element={<MultiAMCWorkOrderDetail />} />
          <Route path="/multi-amc/amcs" element={<MultiAMCDashboard />} />
          <Route path="/multi-amc/api-logs" element={<APILogs />} />
        </Route>

        {/* SIP/OTM Pipeline - SIP & OTM Mandate Form Extraction - Protected for sip-otm/all users only */}
        <Route
          element={
            <SIPOTMProtectedRoute user={user} loading={loading}>
              <SIPOTMLayout user={user} onLogout={handleLogout} />
            </SIPOTMProtectedRoute>
          }
        >
          <Route path="/sip-otm" element={<SIPOTMDashboard />} />
          <Route path="/sip-otm/dashboard" element={<SIPOTMDashboard />} />
          <Route path="/sip-otm/extract" element={<SIPOTMDashboard />} />
          <Route path="/sip-otm/history" element={<SIPOTMWorkOrdersList />} />
          <Route path="/sip-otm/work-order/:workOrderId" element={<SIPOTMWorkOrderDetail />} />
          <Route path="/sip-otm/api-logs" element={<APILogs />} />
        </Route>

        <Route path="*" element={<Navigate to={user ? getDefaultDashboard(user) : "/login"} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
